import React, { useEffect, useState } from "react";
import { api, type Feed } from "../lib/api.ts";

const EMPTY_FEED: Partial<Feed> = {
  name: "", url: "", type: "rss", import_mode: "auto",
  target_ghost_instance: "news", source_type: "government",
  content_fetch_strategy: "auto", processing_tier: "standard",
  polling_interval_minutes: 20, active: 1,
  default_style_mode: "a", companion_enabled: 0,
};

export default function Feeds() {
  const [feeds, setFeeds]       = useState<Feed[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<Feed>>(EMPTY_FEED);
  const [saving, setSaving]     = useState(false);
  const [editId, setEditId]     = useState<number | null>(null);

  function loadFeeds() {
    setLoading(true);
    api.feeds.list()
      .then(setFeeds)
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(loadFeeds, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await api.feeds.update(editId, formData);
      } else {
        await api.feeds.create(formData);
      }
      setShowForm(false);
      setEditId(null);
      setFormData(EMPTY_FEED);
      loadFeeds();
    } catch (err) {
      alert(String(err));
    } finally {
      setSaving(false);
    }
  }

  function handleEdit(feed: Feed) {
    setFormData(feed);
    setEditId(feed.id);
    setShowForm(true);
  }

  async function handleToggleActive(feed: Feed) {
    await api.feeds.update(feed.id, { active: feed.active ? 0 : 1 });
    loadFeeds();
  }

  async function handleDelete(id: number) {
    if (!confirm("Delete this feed? All associated articles will also be deleted.")) return;
    await api.feeds.delete(id);
    loadFeeds();
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Feeds</h1>
          <p className="text-gray-500 text-sm mt-1">{feeds.length} configured</p>
        </div>
        <button onClick={() => { setFormData(EMPTY_FEED); setEditId(null); setShowForm(true); }}
                className="btn-primary btn">
          + Add Feed
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card p-5 mb-5">
          <h2 className="font-medium text-gray-900 mb-4">{editId ? "Edit Feed" : "Add Feed"}</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Name *</label>
              <input className="input" required value={formData.name ?? ""} onChange={e => setFormData(d => ({ ...d, name: e.target.value }))} />
            </div>
            <div>
              <label className="label">RSS URL *</label>
              <input className="input" required type="url" value={formData.url ?? ""} onChange={e => setFormData(d => ({ ...d, url: e.target.value }))} />
            </div>
            <div>
              <label className="label">Import Mode</label>
              <select className="input" value={formData.import_mode ?? "auto"} onChange={e => setFormData(d => ({ ...d, import_mode: e.target.value as "auto" | "manual" }))}>
                <option value="auto">Auto-import</option>
                <option value="manual">Manual-select</option>
              </select>
            </div>
            <div>
              <label className="label">Ghost Instance</label>
              <select className="input" value={formData.target_ghost_instance ?? "news"} onChange={e => setFormData(d => ({ ...d, target_ghost_instance: e.target.value as "news" | "evergreen" }))}>
                <option value="news">News Ghost (News Team)</option>
                <option value="evergreen">Evergreen Ghost (Information Team)</option>
              </select>
            </div>
            <div>
              <label className="label">Source Type</label>
              <select className="input" value={formData.source_type ?? "government"} onChange={e => setFormData(d => ({ ...d, source_type: e.target.value }))}>
                {["government","official_body","corporation","wire_service","aggregator","blog","social","unknown"].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Content Fetch Strategy</label>
              <select className="input" value={formData.content_fetch_strategy ?? "auto"} onChange={e => setFormData(d => ({ ...d, content_fetch_strategy: e.target.value }))}>
                <option value="auto">Auto (Readability → Jina → excerpt)</option>
                <option value="rss_only">RSS excerpt only</option>
                <option value="readability">Readability only</option>
                <option value="jina">Jina only</option>
              </select>
            </div>
            <div>
              <label className="label">Poll interval (minutes)</label>
              <input className="input" type="number" min="5" max="1440" value={formData.polling_interval_minutes ?? 20} onChange={e => setFormData(d => ({ ...d, polling_interval_minutes: parseInt(e.target.value) }))} />
            </div>
            <div>
              <label className="label">Processing Tier</label>
              <select className="input" value={formData.processing_tier ?? "standard"} onChange={e => setFormData(d => ({ ...d, processing_tier: e.target.value as "standard" | "batch" }))}>
                <option value="standard">Standard</option>
                <option value="batch">Batch (Phase 2)</option>
              </select>
            </div>
            <div>
              <label className="label">Default Article Style</label>
              <select className="input" value={formData.default_style_mode ?? "a"} onChange={e => setFormData(d => ({ ...d, default_style_mode: e.target.value as "a" | "b" }))}>
                <option value="a">A — Intelligence Briefing</option>
                <option value="b">B — Magazine Feature</option>
              </select>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input
                type="checkbox"
                id="companion_enabled"
                checked={formData.companion_enabled === 1}
                onChange={e => setFormData(d => ({ ...d, companion_enabled: e.target.checked ? 1 : 0 }))}
                className="rounded border-gray-300 text-brand-500"
              />
              <label htmlFor="companion_enabled" className="text-sm text-gray-700 cursor-pointer">
                Generate companion article by default
                <span className="block text-xs text-gray-400">
                  {formData.default_style_mode === "b"
                    ? "Generates a companion Style A briefing for each feature"
                    : "Generates a companion Style B feature for each briefing"}
                </span>
              </label>
            </div>
            <div className="md:col-span-2 flex gap-3 pt-2">
              <button type="submit" disabled={saving} className="btn-primary btn">
                {saving ? "Saving…" : editId ? "Save Changes" : "Add Feed"}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditId(null); }} className="btn-ghost btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {feeds.length === 0 && (
            <div className="p-6 text-center text-gray-400 text-sm">No feeds configured</div>
          )}
          {feeds.map(feed => (
            <div key={feed.id} className="flex items-center gap-4 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-block w-2 h-2 rounded-full ${feed.active ? "bg-green-400" : "bg-gray-300"}`} />
                  <span className="font-medium text-sm text-gray-900">{feed.name}</span>
                  <span className="badge-gray">{feed.import_mode}</span>
                  <span className="badge-gray">{feed.target_ghost_instance}</span>
                  <span className="badge-gray">{feed.source_type}</span>
                  <span className="badge-gray">Style {(feed.default_style_mode ?? "a").toUpperCase()}</span>
                  {feed.companion_enabled === 1 && (
                    <span className="badge-gray text-xs">+companion</span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{feed.url}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => handleToggleActive(feed)}
                        className="btn-ghost btn btn-sm text-xs">
                  {feed.active ? "Pause" : "Enable"}
                </button>
                <button onClick={() => handleEdit(feed)} className="btn-ghost btn btn-sm text-xs">
                  Edit
                </button>
                <button onClick={() => handleDelete(feed.id)}
                        className="btn-ghost btn btn-sm text-xs text-red-600 hover:bg-red-50">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
